import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_API_V2_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";

export interface InvoiceDocumentVerificationProcessRun {
  status?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
  error_message?: string | null;
  result_payload?: unknown;
}

export interface InvoiceDocumentVerificationProcessResponse {
  duplicate_request?: boolean;
  force_reprocess?: boolean;
  run?: InvoiceDocumentVerificationProcessRun | null;
  existing?: unknown;
  processed?: any[];
  skipped_documents?: any[];
  failed_documents?: any[];
  verification?: unknown;
  [key: string]: unknown;
}

export interface OperationsInvoiceDocument {
  id?: string;
  document_id?: string;
  invoice_id?: string;
  name?: string;
  file_name?: string;
  document_type?: string;
  document_type_name?: string;
  required_document_id?: string;
  map_invoice_required_document_id?: string | null;
  upload_date?: string;
  url?: string;
}

type InvoiceDocumentsQuery =
  | string
  | { invoice_id: string; metadata_only?: boolean };

export const invoiceOnboardingApi = createApi({
  reducerPath: "invoiceOnboardingApi",
  baseQuery: axiosBaseQuery({
    baseUrl: `${VITE_API_V2_URL}`,
  }),
  tagTypes: ["INVOICES"],
  endpoints: (builder) => ({
    getInvoiceListByUserNoParams: builder.query<
      any,
      {
        account_id: string;
        partner_id: string;
        per_page?: number;
        page?: number;
      }
    >({
      query: ({ account_id, partner_id, per_page, page }) => ({
        url: `/operations/accounts/${account_id}/partners/${partner_id}/invoices`,
        method: "get",
        params: { per_page, page },
      }),
      transformResponse: (response) => {
        if (response?.data) {
          const pagination = response.data.pagination || {};
          return {
            data: response.data.invoices || [],
            total: pagination.total || (response.data.invoices?.length ?? 0),
            last_page: pagination.last_page || 1,
            current_page: pagination.current_page || 1,
            per_page: pagination.per_page || 10,
            ...pagination
          };
        }
        return { data: [], total: 0, last_page: 1, current_page: 1, per_page: 10 };
      },
      providesTags: ["INVOICES"],
    }),
    // Proxy to marketplace API for single invoice details
    getInvoiceById: builder.query<any, string>({
      query: (invoice_id) => ({
        url: `/invoices/${invoice_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["INVOICES"],
    }),
    getPartnersByAccount: builder.query<any, string>({
      query: (account_id) => ({
        url: `/operations/accounts/${account_id}/partners`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getInvoiceDetails: builder.query<any, { invoice_id: string }>({
      query: ({ invoice_id }) => ({
        url: `/operations/invoices/${invoice_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getInvoiceMapDetails: builder.query<any, { account_id: string; partner_id: string }>({
      query: ({ account_id, partner_id }) => ({
        url: `/operations/accounts/${account_id}/partners/${partner_id}/map-details`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getPicklists: builder.query<any, void>({
      query: () => ({
        url: `/operations/picklists`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getInvoiceDocuments: builder.query<
      OperationsInvoiceDocument[],
      InvoiceDocumentsQuery
    >({
      query: (arg) => {
        const invoice_id = typeof arg === "string" ? arg : arg.invoice_id;
        const metadata_only =
          typeof arg === "string" ? false : arg.metadata_only;
        return {
          url: `/operations/invoices/${encodeURIComponent(invoice_id)}/all-documents`,
          method: "get",
          params: metadata_only ? { metadata_only: true } : undefined,
        };
      },
      transformResponse: (response) => {
        const data = response?.data;
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.documents)) return data.documents;
        if (Array.isArray(data?.invoice_documents)) return data.invoice_documents;
        if (Array.isArray(data?.supporting_documents)) return data.supporting_documents;
        return [];
      },
    }),
    getInvoiceSupportingDocuments: builder.query<any, string>({
      query: (invoice_id) => ({
        url: `/operations/invoices/${invoice_id}/supporting-documents`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    // Upload supporting documents for invoice-financing
    postInvoiceSupportingDocuments: builder.mutation<
      { invoice_id: string; supporting_documents: [] },
      { file: File; supporting_document_id: string; invoice_id: string }
    >({
      query: (data) => {
        const formData = new FormData();
        formData.append("file", data.file);
        formData.append("required_document_id", data.supporting_document_id);
        return {
          url: `/operations/invoices/${data.invoice_id}/documents`,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      invalidatesTags: ["INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    processInvoiceDocumentVerification: builder.mutation<
      InvoiceDocumentVerificationProcessResponse,
      { invoice_id: string; force?: boolean }
    >({
      query: ({ invoice_id, force }) => ({
        url: `/document-verification/invoices/${invoice_id}/process`,
        method: "POST",
        params: force ? { force: true } : undefined,
      }),
      invalidatesTags: ["INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    // Delete supporting documents for invoice-financing
    deleteInvoiceSupportingDocument: builder.mutation<
      any,
      { invoice_id: string; document_id: string }
    >({
      query: ({ invoice_id, document_id }) => ({
        url: `/operations/invoices/${invoice_id}/documents/${document_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    // Get document URL for viewing
    getInvoiceDocumentUrl: builder.query<
      { url: string },
      { invoice_id: string; document_id: string }
    >({
      query: ({ invoice_id, document_id }) => ({
        url: `/marketplace/invoices/${invoice_id}/documents/${document_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
  }),
});

export const { 
  useGetInvoiceListByUserNoParamsQuery: useGetInvoiceListByUserNoParamsOnboardingQuery,
  useGetInvoiceByIdQuery: useGetInvoiceByIdOnboardingQuery,
  useGetPartnersByAccountQuery,
  useGetInvoiceDetailsQuery,
  useGetInvoiceMapDetailsQuery,
  useGetPicklistsQuery,
  useGetInvoiceDocumentsQuery,
  useLazyGetInvoiceDocumentsQuery,
  useGetInvoiceSupportingDocumentsQuery,
  usePostInvoiceSupportingDocumentsMutation,
  useProcessInvoiceDocumentVerificationMutation,
  useDeleteInvoiceSupportingDocumentMutation,
  useGetInvoiceDocumentUrlQuery,
  useLazyGetInvoiceDocumentUrlQuery,
} = invoiceOnboardingApi;
